// The Maw's dot-matrix display (docs/07 §1.2, §3): 2×2 LEDs with a 1-px gap, amber on aubergine.
// Modes: face (expression + pupils that follow the cursor + blinks), typed text pages, marquee.
import { Container, Sprite, Texture, TilingSprite, type Ticker } from 'pixi.js';
import { PALETTE } from '../../art/palette';
import { glyph, GLYPH_H, GLYPH_W, textWidth, wrap } from '../../art/pixelFont';
import { SPRITES_BY_ID } from '../../art/sprites';
import type { Expression } from '../../sim/types';
import { audio } from '../../audio/audio';
import { clock } from '../timeline/clock';

type Led = 0 | 1 | 2 | 3; // off, lit, pupil, red

const COLORS: Record<Led, number> = { 0: PALETTE.aubergine, 1: PALETTE.brass, 2: PALETTE.gold, 3: PALETTE.red };

export class Display extends Container {
  readonly cols: number;
  readonly rows: number;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private tex: Texture;
  private leds: Led[];
  private mode: 'face' | 'text' | 'marquee' | 'off' = 'off';
  private expression: Expression = 'neutral';
  private gaze = { x: 0, y: 0 };
  private blinkUntil = 0;
  private nextBlink = 0;
  private marqueeText = '';
  private marqueeX = 0;
  private textLines: string[] = [];
  private typed = 0;
  private dirty = true;
  private red = false;
  /** A Collector's portrait replaces the face during their Reckoning. */
  private portrait: string | null = null;
  hasFace = false;

  constructor(
    cols: number,
    rows: number,
    /** LED pitch in px: 3, or 2 on the compact 5×4/5×5 cabinets. */
    readonly pitch = 3,
  ) {
    super();
    const PITCH = pitch;
    this.cols = cols;
    this.rows = rows;
    this.leds = new Array(cols * rows).fill(0);
    this.canvas = document.createElement('canvas');
    this.canvas.width = cols;
    this.canvas.height = rows;
    this.ctx = this.canvas.getContext('2d')!;
    this.tex = Texture.from(this.canvas);
    this.tex.source.scaleMode = 'nearest';
    const img = new Sprite(this.tex);
    img.scale.set(PITCH);
    const grid = document.createElement('canvas');
    grid.width = PITCH;
    grid.height = PITCH;
    const g = grid.getContext('2d')!;
    g.fillStyle = '#' + PALETTE.ink.toString(16).padStart(6, '0');
    g.fillRect(PITCH - 1, 0, 1, PITCH);
    g.fillRect(0, PITCH - 1, PITCH, 1);
    const gridTex = Texture.from(grid);
    gridTex.source.scaleMode = 'nearest';
    const overlay = new TilingSprite({ texture: gridTex, width: cols * PITCH, height: rows * PITCH });
    this.addChild(img, overlay);
    this.scheduleBlink(performance.now());
  }

  get pxWidth(): number {
    return this.cols * this.pitch;
  }
  get pxHeight(): number {
    return this.rows * this.pitch;
  }

  setRed(on: boolean): void {
    this.red = on;
    this.dirty = true;
  }

  showFace(expression: Expression = this.expression): void {
    this.expression = expression;
    this.mode = 'face';
    this.dirty = true;
  }

  setExpression(expression: Expression): void {
    this.expression = expression;
    this.dirty = true;
  }

  setPortrait(id: string | null): void {
    if (this.portrait === id) return;
    this.portrait = id && SPRITES_BY_ID.has(`portrait_${id}`) ? id : null;
    this.dirty = true;
  }

  get showingPortrait(): string | null {
    return this.portrait;
  }

  /** Gaze in -1..1 per axis (cursor relative to the display). */
  look(x: number, y: number): void {
    const gx = x < -0.33 ? -1 : x > 0.33 ? 1 : 0;
    const gy = y < -0.33 ? -1 : y > 0.33 ? 1 : 0;
    if (gx !== this.gaze.x || gy !== this.gaze.y) {
      this.gaze = { x: gx, y: gy };
      this.dirty = true;
    }
  }

  blank(): void {
    this.mode = 'off';
    this.dirty = true;
  }

  marquee(text: string): void {
    this.mode = 'marquee';
    this.marqueeText = text.toUpperCase();
    this.marqueeX = this.cols;
    this.dirty = true;
  }

  /** Type a line (≤ 2 wrapped lines per page, 30 chars/s, one blip per char). */
  async say(text: string, pitch = 0): Promise<void> {
    if (!this.hasFace) {
      this.marquee(text);
      await clock.wait(Math.max(1500, (textWidth(text) + this.cols) * 30));
      return;
    }
    const perLine = Math.floor((this.cols + 1) / (GLYPH_W + 1));
    const lines = wrap(text.toUpperCase(), perLine);
    for (let p = 0; p < lines.length; p += 2) {
      this.mode = 'text';
      this.textLines = lines.slice(p, p + 2);
      this.typed = 0;
      const total = this.textLines.join('').length;
      for (let i = 0; i < total; i++) {
        this.typed = i + 1;
        this.dirty = true;
        if (!clock.isSkipping && i % 2 === 0) audio.play('blip', { pitch, volume: 0.35 });
        await clock.wait(33);
      }
      await clock.wait(900);
    }
    this.showFace();
  }

  private scheduleBlink(now: number): void {
    this.nextBlink = now + 3000 + Math.random() * 3000;
  }

  update = (t: Ticker) => {
    const now = performance.now();
    if (this.mode === 'face' && now > this.nextBlink && !this.portrait) {
      this.blinkUntil = now + 140;
      this.scheduleBlink(now);
      this.dirty = true;
    }
    if (this.mode === 'face' && this.blinkUntil && now > this.blinkUntil) {
      this.blinkUntil = 0;
      this.dirty = true;
    }
    if (this.mode === 'marquee') {
      this.marqueeX -= t.deltaMS * 0.03;
      if (this.marqueeX < -textWidth(this.marqueeText)) this.marqueeX = this.cols;
      this.dirty = true;
    }
    if (this.dirty) this.redraw();
  };

  private redraw(): void {
    this.dirty = false;
    this.leds.fill(0);
    if (this.mode === 'face') this.drawFace();
    else if (this.mode === 'text') this.drawText();
    else if (this.mode === 'marquee') this.drawString(this.marqueeText, Math.round(this.marqueeX), Math.floor((this.rows - GLYPH_H) / 2));
    const img = this.ctx.createImageData(this.cols, this.rows);
    for (let i = 0; i < this.leds.length; i++) {
      let led = this.leds[i]!;
      if (this.red && led === 1) led = 3;
      const c = COLORS[led];
      img.data[i * 4] = (c >> 16) & 255;
      img.data[i * 4 + 1] = (c >> 8) & 255;
      img.data[i * 4 + 2] = c & 255;
      img.data[i * 4 + 3] = 255;
    }
    this.ctx.putImageData(img, 0, 0);
    this.tex.source.update();
  }

  private set(x: number, y: number, v: Led): void {
    if (x < 0 || y < 0 || x >= this.cols || y >= this.rows) return;
    this.leds[y * this.cols + x] = v;
  }

  private drawString(s: string, x0: number, y0: number, maxChars = Infinity): void {
    let x = x0;
    let n = 0;
    for (const ch of s) {
      if (n++ >= maxChars) break;
      const g = glyph(ch);
      for (let gy = 0; gy < GLYPH_H; gy++) for (let gx = 0; gx < GLYPH_W; gx++) if (g[gy]![gx] === '#') this.set(x + gx, y0 + gy, 1);
      x += GLYPH_W + 1;
    }
  }

  private drawText(): void {
    const lh = GLYPH_H + 3;
    const total = this.textLines.length * lh - 3;
    let y = Math.floor((this.rows - total) / 2);
    let left = this.typed;
    for (const line of this.textLines) {
      const x = Math.floor((this.cols - textWidth(line)) / 2);
      this.drawString(line, x, y, left);
      left -= line.length;
      y += lh;
    }
  }

  private drawFace(): void {
    const id = this.portrait ? `portrait_${this.portrait}` : `face_${this.expression}`;
    const def = SPRITES_BY_ID.get(id) ?? SPRITES_BY_ID.get('face_neutral');
    if (!def) return;
    const rows = def.frames[0]!;
    const ox = Math.floor((this.cols - def.w) / 2);
    const oy = Math.floor((this.rows - def.h) / 2);
    const blinking = this.blinkUntil > 0;
    const blink = blinking ? SPRITES_BY_ID.get('face_blink')?.frames[0] : undefined;
    const socket = (x: number, y: number) => {
      const ch = rows[y]?.[x];
      return ch === 'e' || ch === 'p';
    };
    const dx = this.gaze.x * 2;
    const dy = this.gaze.y;
    for (let y = 0; y < def.h; y++)
      for (let x = 0; x < def.w; x++) {
        const ch = rows[y]![x]!;
        if (blink && socket(x, y)) continue;
        if (ch === '#') this.set(ox + x, oy + y, 1);
        else if (ch === 'r') this.set(ox + x, oy + y, 3);
        else if (ch === 'p' && !blink) {
          const tx = x + dx;
          const ty = y + dy;
          if (socket(tx, ty)) this.set(ox + tx, oy + ty, 2);
          else this.set(ox + x, oy + y, 2);
        }
      }
    if (blink) {
      // Close only the eye area: draw the blink lines over sockets, keep the mouth.
      for (let y = 0; y < def.h; y++)
        for (let x = 0; x < def.w; x++) {
          if (!socket(x, y) && !(rows[y]![x] === '#' && isEyeRim(rows, x, y))) continue;
          this.set(ox + x, oy + y, blink[y]?.[x] === '#' ? 1 : 0);
        }
    }
  }
}

/** A lit pixel directly bordering an eye socket counts as eye rim (hidden during blinks). */
function isEyeRim(rows: string[], x: number, y: number): boolean {
  for (let dy = -1; dy <= 1; dy++)
    for (let dx = -1; dx <= 1; dx++) {
      const c = rows[y + dy]?.[x + dx];
      if (c === 'e' || c === 'p') return true;
    }
  return false;
}
